import { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flexRender, Table as ReactTable } from '@tanstack/react-table';
import SSRIcon from '@ingka/ssr-icon';
import ChevronDown from '@ingka/ssr-icon/paths/chevron-down';
import ChevronUp from '@ingka/ssr-icon/paths/chevron-up';
import styles from './index.module.css';
import { useTranslation } from 'react-i18next';
import { TablePaginationControls } from '../skapa-table/pagination';

type GroupedRow<T> = {
  childRows?: T[];
};

type TableProps<T extends GroupedRow<T>> = {
  table: ReactTable<T>;
  gridColumns: string;
  stickyLeftColumns?: number;
  stickyRightColumns?: number;
  className?: string;
  showHeaders?: boolean;
};

type StickyOffsets = {
  left: number[];
  right: number[];
};

const checkSticky = (
  index: number,
  leftOffset: number | undefined,
  rightOffset: number | undefined,
  length: number
) => {
  const isStickyLeft = leftOffset ? index < leftOffset : false;
  const isStickyRight = rightOffset ? length - index <= rightOffset : false;
  return {
    isStickyLeft,
    isStickyRight,
    isLastLeftSticky: index + 1 === leftOffset,
    isLastRightSticky: length - index === rightOffset,
  };
};

const getReferenceCells = (
  container: HTMLDivElement,
  showHeaders: boolean,
  totalColumns: number
) => {
  if (totalColumns === 0) return [];

  const children = Array.from(container.children);

  if (showHeaders) {
    return children
      .slice(0, totalColumns)
      .filter(
        (child): child is HTMLDivElement => child instanceof HTMLDivElement
      );
  }

  const [firstRow] = children;

  return Array.from(firstRow.children).filter(
    (child): child is HTMLDivElement => child instanceof HTMLDivElement
  );
};

const createStickyOffsets = (cells: HTMLDivElement[]): StickyOffsets => {
  const widths = cells.map((cell) => cell.getBoundingClientRect().width);
  const left = new Array<number>(widths.length).fill(0);
  const right = new Array<number>(widths.length).fill(0);

  let leftOffset = 0;
  for (let index = 0; index < widths.length; index += 1) {
    left[index] = leftOffset;
    leftOffset += widths[index];
  }

  let rightOffset = 0;
  for (let index = widths.length - 1; index >= 0; index -= 1) {
    right[index] = rightOffset;
    rightOffset += widths[index];
  }

  return { left, right };
};

const areStickyOffsetsEqual = (
  currentOffsets: StickyOffsets | null,
  nextOffsets: StickyOffsets
) =>
  currentOffsets !== null &&
  currentOffsets.left.length === nextOffsets.left.length &&
  currentOffsets.right.length === nextOffsets.right.length &&
  currentOffsets.left.every(
    (value, index) => value === nextOffsets.left[index]
  ) &&
  currentOffsets.right.every(
    (value, index) => value === nextOffsets.right[index]
  );

const TableCell: FC<{
  children: React.ReactNode;
  index: number;
  totalColumns: number;
  stickyLeftColumns?: number;
  stickyRightColumns?: number;
  stickyOffsets: StickyOffsets | null;
  sortable?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}> = ({
  children,
  index,
  totalColumns,
  stickyLeftColumns,
  stickyRightColumns,
  stickyOffsets,
  sortable,
  className = '',
  onClick,
}) => {
  const { isStickyLeft, isStickyRight, isLastLeftSticky, isLastRightSticky } =
    useMemo(
      () =>
        checkSticky(index, stickyLeftColumns, stickyRightColumns, totalColumns),
      [index, stickyLeftColumns, stickyRightColumns, totalColumns]
    );

  const isSticky = isStickyLeft || isStickyRight;
  const lastLeftSticky = isStickyLeft && isLastLeftSticky;
  const lastRightSticky = isStickyRight && isLastRightSticky;
  const stickyLeftCss = isStickyLeft ? 'sticky top-0 self-center' : '';
  const stickyRightCss = isStickyRight ? 'sticky top-0 self-center' : '';
  const stickySeparatorLeftCss = lastLeftSticky
    ? 'border-r-2 border-skapa-neutral-4'
    : '';
  const stickySeparatorRightCss = lastRightSticky
    ? 'border-l-2 border-skapa-neutral-4'
    : '';
  const left = stickyOffsets?.left[index] ?? 0;
  const right = stickyOffsets?.right[index] ?? 0;

  return (
    <div
      role="cell"
      className={`w-full h-full ${styles['table-grouped__cell']} ${
        isSticky ? 'z-30' : 'z-0'
      } ${stickyLeftCss} ${stickyRightCss} ${stickySeparatorLeftCss} ${stickySeparatorRightCss}`}
      style={{
        left: isStickyLeft ? `${left}px` : undefined,
        right: isStickyRight ? `${right}px` : undefined,
      }}
    >
      <div
        className={`w-full h-full flex items-center py-2 px-2 ${className} ${
          sortable ? 'cursor-pointer' : ''
        }`}
        onClick={onClick}
      >
        {children}
      </div>
    </div>
  );
};

export function SkapaGroupedTable<T extends GroupedRow<T>>({
  table,
  gridColumns,
  stickyLeftColumns,
  stickyRightColumns,
  className = '',
  showHeaders = true,
}: TableProps<T>) {
  // Hooks
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [stickyOffsets, setStickyOffsets] = useState<StickyOffsets | null>(
    null
  );

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const paginationRows = table.getPaginationRowModel().rows;
  const totalColumns = showHeaders
    ? headerGroups[0]?.headers.length ?? rows[0]?.getVisibleCells().length ?? 0
    : rows[0]?.getVisibleCells().length ?? headerGroups[0]?.headers.length ?? 0;
  const referenceRowKey = showHeaders ? headerGroups[0]?.id : rows[0]?.id;

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container || totalColumns === 0) {
      setStickyOffsets((currentOffsets) =>
        currentOffsets === null ? currentOffsets : null
      );
      return;
    }

    const measureOffsets = () => {
      const referenceCells = getReferenceCells(
        container,
        showHeaders,
        totalColumns
      );

      if (referenceCells.length !== totalColumns) {
        return;
      }

      const nextOffsets = createStickyOffsets(referenceCells);
      setStickyOffsets((currentOffsets) =>
        areStickyOffsetsEqual(currentOffsets, nextOffsets)
          ? currentOffsets
          : nextOffsets
      );
    };

    measureOffsets();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    let frameId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(measureOffsets);
    });

    getReferenceCells(container, showHeaders, totalColumns).forEach((cell) => {
      observer.observe(cell);
    });

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [gridColumns, referenceRowKey, showHeaders, totalColumns]);

  return (
    <div className={`w-full overflow-auto ${className}`}>
      <div
        ref={containerRef}
        className={`${styles['table-grouped']} grid`}
        style={{
          gridTemplateColumns: gridColumns,
        }}
      >
        {showHeaders &&
          headerGroups.map((headerGroup) =>
            headerGroup.headers.map((header, index, arr) => (
              <TableCell
                key={header.id}
                index={index}
                totalColumns={arr.length}
                stickyLeftColumns={stickyLeftColumns}
                stickyRightColumns={stickyRightColumns}
                stickyOffsets={stickyOffsets}
                sortable={header.column.getCanSort()}
                className={`bg-skapa-neutral-1 border-b border-skapa-neutral-4 ${
                  header.column.columnDef.meta?.headerClassName || ''
                }`}
                onClick={header.column.getToggleSortingHandler()}
              >
                {header.isPlaceholder ? null : (
                  <div className="w-full flex items-center justify-between">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getCanSort() && (
                      <div className="flex flex-col ml-[10px] h-6">
                        <SSRIcon
                          paths={ChevronUp}
                          className={
                            styles[
                              header.column.getIsSorted() === 'asc'
                                ? 'fill--selected'
                                : 'fill'
                            ]
                          }
                        />
                        <SSRIcon
                          paths={ChevronDown}
                          className={
                            styles[
                              header.column.getIsSorted() === 'desc'
                                ? 'fill--selected'
                                : 'fill'
                            ]
                          }
                        />
                      </div>
                    )}
                    {/* header.column.getIsSorted() */}
                  </div>
                )}
              </TableCell>
            ))
          )}

        {rows.map((row) => {
          const isGroup = Array.isArray(row.original.childRows);

          return (
            <div
              key={row.id}
              data-testid={isGroup ? 'grouped-parent-row' : 'grouped-child-row'}
              className={`${styles['table-grouped__row']} ${
                styles[
                  isGroup
                    ? 'table-grouped__row--parent'
                    : 'table-grouped__row--child'
                ]
              }`}
            >
              {row.getVisibleCells().map((cell, index, arr) => (
                <TableCell
                  key={cell.id}
                  index={index}
                  totalColumns={arr.length}
                  stickyLeftColumns={stickyLeftColumns}
                  stickyRightColumns={stickyRightColumns}
                  stickyOffsets={stickyOffsets}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </div>
          );
        })}
      </div>

      {paginationRows.length > 0 && (
        <TablePaginationControls
          className="sticky left-0 gap-2 mt-3 px-4 pb-10"
          table={table}
        />
      )}

      {rows.length === 0 && (
        <div className="py-10 text-center text-skapa-text-2">
          {t('label.noData')}
        </div>
      )}
    </div>
  );
}
